import { cn } from '@/lib/utils';

type ToggleValue = 'left' | 'middle' | 'right';

interface ToggleSwitchProps {
  value: ToggleValue;
  leftLabel: string;
  rightLabel: string;
  middleLabel?: string;
  onChange: (value: ToggleValue) => void;
  /** 2-state mode: left/right only (no middle). 3-state: left/middle/right */
  mode?: '2-state' | '3-state';
  className?: string;
}

/**
 * Segmented multi-state control. All states are visible at once with the
 * current one highlighted, so it reads as a toggle (not a one-shot button).
 * Clicking a segment selects that state directly; ArrowLeft/ArrowRight move
 * the selection. #4270
 */
export function ToggleSwitch({
  value,
  leftLabel,
  rightLabel,
  middleLabel = 'Skip',
  onChange,
  mode = '3-state',
  className,
}: ToggleSwitchProps) {
  const segments: { v: ToggleValue; label: string }[] =
    mode === '2-state'
      ? [
          { v: 'left', label: leftLabel },
          { v: 'right', label: rightLabel },
        ]
      : [
          { v: 'left', label: leftLabel },
          { v: 'middle', label: middleLabel },
          { v: 'right', label: rightLabel },
        ];

  const selectedClass = (v: ToggleValue) => {
    if (mode === '2-state') {
      if (v === 'left') return 'bg-[#290BD9] text-white'; // Cobalt = approve existing
      if (v === 'right') return 'bg-red-600 text-white'; // Red = undo
      return 'bg-gray-400 text-white';
    }
    if (v === 'left') return 'bg-green-600 text-white'; // Green = approve (action)
    if (v === 'right') return 'bg-red-600 text-white'; // Red = reject
    return 'bg-gray-500 text-white'; // Grey = skip/tbd
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'ArrowLeft') {
      e.preventDefault();
      if (mode === '2-state') onChange('left');
      else onChange(value === 'right' ? 'middle' : 'left');
    } else if (e.key === 'ArrowRight') {
      e.preventDefault();
      if (mode === '2-state') onChange('right');
      else onChange(value === 'left' ? 'middle' : 'right');
    }
  };

  return (
    <div
      role="radiogroup"
      onClick={(e) => e.stopPropagation()}
      onKeyDown={handleKeyDown}
      className={cn(
        'inline-flex items-center gap-0.5 rounded-full border border-gray-200 bg-gray-50 p-0.5 shrink-0',
        className,
      )}
    >
      {segments.map((seg) => {
        const isSelected = seg.v === value;
        return (
          <button
            key={seg.v}
            type="button"
            role="radio"
            aria-checked={isSelected}
            onClick={(e) => {
              e.stopPropagation();
              if (!isSelected) onChange(seg.v);
            }}
            className={cn(
              'h-6 rounded-full px-2.5 text-[10px] font-semibold transition-colors cursor-pointer select-none focus:outline-none focus-visible:ring-2 focus-visible:ring-[#290BD9]',
              isSelected ? selectedClass(seg.v) : 'text-gray-500 hover:text-gray-700',
            )}
          >
            {seg.label}
          </button>
        );
      })}
    </div>
  );
}
